import { nextMessageId } from '../chat/messageId.ts'
import { encryptControlMessage } from '../crypto/zionPrivacyCrypto.ts'
import { setMessageTimerTtl } from '../chat/messageTimer.ts'
import {
  buildEphemeralTimerFields,
  resolveDurationMsFromPayload,
} from './ephemeralPayload.ts'
import { ensureContactWithPublicKey, isContactPublicKeyMissing } from '../contacts/contactKeyService.ts'
import { getCurrentNickname } from '../identity/ZionIdentityManager.ts'
import { notifyIncomingMessage, notifyTempIncomingMessage } from '../notifications/messageNotify.ts'
import { findContact, findContactBySession, type ZionContact } from '../storage/pairingStore.ts'
import { getPrivateKey, getPublicKeyBase64 } from '../storage/keyStore.ts'
import { appendMessage, deleteMessagesForContact, messageExists, patchMessage, type ChatMessage } from '../storage/chatStore.ts'
import { getSessionToken } from '../storage/sessionStore.ts'
import { clearUnread, incrementUnread } from '../storage/unreadStore.ts'
import {
  buildTempSession,
  findActiveTempForContact,
  findPendingOutgoingByLane,
  findPendingOutgoingTempForPeer,
  getTempChatByKey,
  listPendingOutgoingTempChats,
  listTempChats,
  removeTempChat,
  resolveTempChatKey,
  sessionsEqual,
  upsertTempChat,
  type TempChatSession,
} from '../storage/tempChatStore.ts'
import {
  connectSocket,
  pollInboxBurst,
  pushBlob,
  sendTempChatAccept,
  sendTempChatCreate,
  sendTempChatExpired,
  sendTempChatMessage,
  sendTempChatReject,
  setTempChatHandlers,
} from '../socket2/ZionSocketClient.ts'
import { buildNativeApnsPayload, wakePeerBySession } from '../push/pushClient.ts'
import { navigate } from '../ui/router.ts'
import {
  buildTempChatAck,
  buildTempChatCancel,
  buildTempChatMsg,
  buildTempChatRej,
  buildTempChatReq,
  parseTempChatControl,
} from './tempChatControl.ts'
import {
  formatTempRemaining,
  TEMP_CHAT_DURATIONS,
  TEMP_CHAT_FALLBACK_MS,
  tempChatMessageContactId,
  tempChatUnreadId,
} from './tempChatUtils.ts'

let expiryTimer: number | null = null
let getCurrentTempChatKeyFn: () => string = () => ''

/** Chiavi in fase di accettazione — blocca cancel/reject concorrenti. */
const acceptInProgressKeys = new Set<string>()
/** Handshake completato: blocca reject automatici per ~90s. */
const acceptedHandshakeUntil = new Map<string, number>()

const HANDSHAKE_GRACE_MS = 90_000

function markHandshakeAccepted(chatKey: string): void {
  const key = chatKey.trim().toLowerCase()
  if (!key) return
  acceptedHandshakeUntil.set(key, Date.now() + HANDSHAKE_GRACE_MS)
}

function isHandshakeProtected(chatKey: string): boolean {
  const key = chatKey.trim().toLowerCase()
  if (!key) return false
  const until = acceptedHandshakeUntil.get(key) ?? 0
  if (until <= Date.now()) {
    acceptedHandshakeUntil.delete(key)
    return false
  }
  return true
}

function isAcceptInProgress(chatKey: string): boolean {
  return acceptInProgressKeys.has(chatKey.trim().toLowerCase())
}

/** Rimuove altre temp pending dello stesso contatto senza inviare reject di rete. */
async function dismissLocalPendingTempsForContact(
  contactId: string,
  exceptChatKey: string,
): Promise<void> {
  const cid = contactId.trim()
  const keep = exceptChatKey.trim().toLowerCase()
  for (const temp of listTempChats()) {
    if (temp.contactId !== cid) continue
    if (temp.chatKey.trim().toLowerCase() === keep) continue
    if (temp.status !== 'pending_outgoing' && temp.status !== 'pending_incoming') continue
    await deleteMessagesForContact(tempChatMessageContactId(temp.chatKey))
    removeTempChat(temp.chatKey)
  }
}

/** Dev-only: log locale — niente POST /debug in produzione (P0 security). */
async function traceTempWire(
  phase: string,
  wire: string,
  meta: Record<string, unknown> = {},
): Promise<void> {
  if (import.meta.env.PROD) return
  const parsed = parseTempChatControl(wire)
  const lane = parsed?.lane ?? (wire.includes('|') ? wire.split('|')[1]?.trim() : wire.trim())
  const payload = { phase, wire, lane, chatKey: lane, action: parsed?.action, ...meta }
  console.log('[ZION-WIRE-TRACE]', phase, payload)
}

export function bindTempChatContext(opts: { getCurrentTempChatKey: () => string }): void {
  getCurrentTempChatKeyFn = opts.getCurrentTempChatKey
}

export function installTempChatHandlers(): void {
  setTempChatHandlers({
    onCreate: (data) => {
      void handleIncomingTempChatCreate(data)
    },
    onMessage: (data) => {
      void handleIncomingTempMessage(data)
    },
    onExpired: (data) => {
      void handleTempChatExpired(String(data.chatKey ?? ''))
    },
    onAccept: (data) => {
      void handleIncomingTempChatAccept(data)
    },
    onReject: (data) => {
      void handleIncomingTempChatReject(data)
    },
  })
  startTempChatExpiryLoop()
}

export function startTempChatExpiryLoop(): void {
  if (expiryTimer != null) return
  expiryTimer = window.setInterval(() => {
    void purgeExpiredTempChats()
  }, 1000)
}

function durationLabel(ms: number): string {
  return TEMP_CHAT_DURATIONS.find((d) => d.ms === ms)?.label ?? formatTempRemaining(ms)
}

async function appendTempSystemMessage(chatKey: string, text: string): Promise<void> {
  const msgContactId = tempChatMessageContactId(chatKey)
  const id = `temp-sys-${Date.now()}`
  await appendMessage({
    id,
    contactId: msgContactId,
    text,
    outgoing: false,
    timestamp: Date.now(),
    isTemp: true,
  })
}

async function resolvePeerForTempInvite(contactId: string): Promise<{
  contact: ZionContact
  mySession: string
  canWire: boolean
} | { error: string }> {
  const contact = await findContact(contactId.trim())
  if (!contact) return { error: 'Contatto non trovato.' }
  if (!contact.sessionToken?.trim()) {
    return { error: 'Sessione contatto mancante. Riaccoppia il contatto.' }
  }

  let resolvedContact = contact
  if (isContactPublicKeyMissing(contact.publicKey)) {
    try {
      resolvedContact = await ensureContactWithPublicKey(contact)
    } catch {
      /* fetch fallito — canWire resta false */
    }
  }
  const canWire = !!(resolvedContact.inboundQueueId?.trim() && resolvedContact.publicKey?.trim())

  const mySession = (await getSessionToken())?.trim() ?? ''
  if (!mySession) return { error: 'Sessione non pronta.' }

  const peerSession = contact.sessionToken.trim()
  if (peerSession.toLowerCase() === mySession.toLowerCase()) {
    return { error: 'Contatto non valido (stessa sessione). Riaccoppia il contatto.' }
  }

  try {
    const myPub = (await getPublicKeyBase64()).trim()
    if (myPub && resolvedContact.publicKey.trim() === myPub) {
      return { error: 'Non puoi inviare una chat a tempo a te stesso.' }
    }
  } catch {
    /* ignore */
  }

  return { contact: resolvedContact, mySession, canWire }
}

export { isTempChatControlPlain } from './tempChatControl.ts'

async function isTempControlFromPeer(fromSession: string): Promise<boolean> {
  const mySession = (await getSessionToken())?.trim() ?? ''
  if (!mySession || !fromSession.trim()) return false
  return !sessionsEqual(fromSession, mySession)
}

function laneFromTempEvent(data: Record<string, unknown>): string {
  return String(data.chatKey ?? data.lane ?? data.key ?? '').trim().toLowerCase()
}

/** Risolve la sessione del peer remoto anche se il server inverte from/to nel fanout SSE. */
async function resolveTempEventPeerSession(
  data: Record<string, unknown>,
  opts: { preferAcceptor?: boolean; preferInitiator?: boolean } = {},
): Promise<string> {
  const mySession = (await getSessionToken())?.trim() ?? ''
  const lane = laneFromTempEvent(data)

  const acceptor = String(data.acceptorSessionToken ?? '').trim()
  const initiator = String(data.initiatorSessionToken ?? data.creatorSessionToken ?? '').trim()
  const outerFrom = String(data.fromSessionToken ?? '').trim()
  const outerTo = String(data.toSessionToken ?? '').trim()
  const payloadFrom = String(data.mySessionToken ?? data.senderSessionToken ?? '').trim()

  const ordered: string[] = []
  if (opts.preferAcceptor && acceptor) ordered.push(acceptor)
  if (opts.preferInitiator && initiator) ordered.push(initiator)
  for (const s of [outerFrom, payloadFrom, acceptor, initiator, outerTo]) {
    if (s && !ordered.includes(s)) ordered.push(s)
  }

  for (const candidate of ordered) {
    if (mySession && sessionsEqual(candidate, mySession)) continue
    return candidate
  }

  if (lane) {
    const byLane = findPendingOutgoingByLane(lane) ?? getTempChatByKey(lane)
    if (byLane?.peerSessionToken && !sessionsEqual(byLane.peerSessionToken, mySession)) {
      return byLane.peerSessionToken.trim()
    }
  }

  const pending = listPendingOutgoingTempChats()[0]
  if (pending?.peerSessionToken && !sessionsEqual(pending.peerSessionToken, mySession)) {
    return pending.peerSessionToken.trim()
  }

  return ordered.find((s) => s && !sessionsEqual(s, mySession)) ?? ordered[0] ?? ''
}

async function findContactForTempChat(fromSession: string): Promise<ZionContact | null> {
  return findContactBySession(fromSession)
}

function durationMsForWireControl(controlPlain: string, contactId: string): number {
  const wire = parseTempChatControl(controlPlain)
  if (!wire) return TEMP_CHAT_FALLBACK_MS
  if (wire.action === 'REQ' && wire.expiresAt && wire.expiresAt > Date.now()) {
    return Math.max(60_000, wire.expiresAt - Date.now())
  }
  const byLane = getTempChatByKey(wire.lane)
  const byContact = listTempChats().find((t) => t.contactId === contactId.trim())
  const temp = byLane ?? byContact
  if (temp?.durationMs && temp.durationMs > 0) return temp.durationMs
  if (temp?.expiresAt && temp.expiresAt > Date.now()) {
    return Math.max(60_000, temp.expiresAt - Date.now())
  }
  return TEMP_CHAT_FALLBACK_MS
}

async function sendTempChatWireToContact(
  contactId: string,
  controlPlain: string,
): Promise<{ ok: boolean; delivered: boolean }> {
  const raw = await findContact(contactId)
  if (!raw?.inboundQueueId) return { ok: false, delivered: false }
  let contact: ZionContact
  try {
    contact = await ensureContactWithPublicKey(raw)
  } catch {
    return { ok: false, delivered: false }
  }
  const privateKey = await getPrivateKey()
  const session = (await getSessionToken()) ?? ''
  const messageId = await nextMessageId()
  const parsed = parseTempChatControl(controlPlain)
  const tracePhase =
    parsed?.action === 'REQ'
      ? 'INVIO_INVITO_SU_WIRE'
      : parsed?.action === 'ACK'
        ? 'INVIO_ACK_SU_WIRE'
        : parsed?.action === 'CANCEL' || parsed?.action === 'REJ'
          ? 'INVIO_CANCEL_REJ_SU_WIRE'
          : 'INVIO_WIRE'
  void traceTempWire(tracePhase, controlPlain, {
    toSession: contact.sessionToken,
    fromSession: session,
    messageId,
    contactId,
    toNickname: contact.nickname,
  })
  // Wire cifrato con envelope JSON + campi ttl ridondanti (compat Android).
  const durationMs = durationMsForWireControl(controlPlain, contactId)
  const blob = await encryptControlMessage(
    privateKey,
    controlPlain,
    contact.publicKey,
    session,
    messageId,
    durationMs,
  )
  const fromNick = (await getCurrentNickname()).trim()
  const pushOk = await pushBlob(contact.inboundQueueId, blob, contact.sessionToken, 'text', {
    senderSessionToken: session,
    messageId,
    toNickname: contact.nickname,
    fromNickname: fromNick,
  })
  let relayDelivered = false
  try {
    const { relayPrivateBlob } = await import('../socket2/ZionSocketClient.ts')
    const relay = await relayPrivateBlob({
      toSessionToken: contact.sessionToken,
      fromSessionToken: session,
      blob,
      messageId,
      toNickname: contact.nickname,
      fromNickname: fromNick,
    })
    relayDelivered = relay.delivered
  } catch {
    /* relay opzionale se server non patchato */
  }
  const ok = relayDelivered || pushOk
  void traceTempWire(tracePhase + '_RESULT', controlPlain, {
    toSession: contact.sessionToken,
    messageId,
    pushOk,
    relayDelivered,
    ok,
  })
  if (ok) {
    void wakePeerBySession(contact.sessionToken, buildNativeApnsPayload('ZION', '⏱ Chat a tempo'))
  }
  return { ok, delivered: relayDelivered || pushOk }
}

export function processTempChatControlFromPlain(
  plain: string,
  contact: ZionContact,
): boolean {
  const wire = parseTempChatControl(plain)
  if (!wire) return false
  void (async () => {
    const fromSession = contact.sessionToken
    if (!(await isTempControlFromPeer(fromSession))) return

    if (wire.action === 'REQ') {
      void traceTempWire('RICEVUTO_DA_TEL', plain, {
        action: wire.action,
        fromSession,
        contactId: contact.contactId,
        fromNickname: contact.nickname,
      })
      await handleIncomingTempChatCreate(
        {
          chatKey: wire.lane,
          expiresAt: wire.expiresAt,
          inviteCode: wire.code,
          fromSessionToken: fromSession,
          myNickname: contact.nickname,
        },
        contact,
      )
    } else if (wire.action === 'ACK') {
      void traceTempWire('RICEVUTO_ACK_DA_TEL', plain, {
        action: wire.action,
        lane: wire.lane,
        fromSession: contact.sessionToken,
        contactId: contact.contactId,
      })
      const chatKey = resolveTempChatKey(contact.contactId, wire.lane)
      const temp = getTempChatByKey(chatKey)
      const durationMs = temp?.durationMs ?? TEMP_CHAT_FALLBACK_MS
      await handleIncomingTempChatAccept({
        chatKey,
        lane: wire.lane,
        fromSessionToken: contact.sessionToken,
        acceptorSessionToken: contact.sessionToken,
        ...buildEphemeralTimerFields(durationMs),
      })
    } else if (wire.action === 'REJ' || wire.action === 'CANCEL') {
      if (!(await isTempControlFromPeer(contact.sessionToken))) return
      const chatKey = resolveTempChatKey(contact.contactId, wire.lane)
      await handleIncomingTempChatReject({
        chatKey,
        lane: wire.lane,
        fromSessionToken: contact.sessionToken,
        userInitiated: true,
      })
    } else if (wire.action === 'MSG') {
      const chatKey = resolveTempChatKey(contact.contactId, wire.lane)
      await handleIncomingTempMessage({
        chatKey,
        message: wire.message,
        messageId: wire.messageId,
        fromSessionToken: fromSession,
      })
    }
  })()
  return true
}

export async function startTempChatWithContact(
  contactId: string,
  durationMs: number,
): Promise<{ ok: boolean; chatKey?: string; error?: string }> {
  const resolved = await resolvePeerForTempInvite(contactId)
  if ('error' in resolved) return { ok: false, error: resolved.error }
  const { contact, canWire } = resolved

  const { ensurePrivacySession } = await import('../app/zionApp.ts')
  let mySession = await ensurePrivacySession()
  await connectSocket(mySession)

  const existing = listTempChats().find((s) => s.contactId === contactId.trim())
  if (existing) removeTempChat(existing.chatKey)

  const peerSession = contact.sessionToken.trim()
  const expiresAt = Date.now() + durationMs
  let session = buildTempSession(contactId, mySession, peerSession, expiresAt, durationMs)
  upsertTempChat(session)

  const nickname = await getCurrentNickname()
  const reqPlain = buildTempChatReq(session.chatKey, session.inviteCode ?? '', expiresAt)
  void traceTempWire('INVIO_INVITO_SU_WIRE', reqPlain, {
    chatKey: session.chatKey,
    toSession: peerSession,
    contactId,
    canWire,
  })

  // Wire E2E first — stesso protocollo telefono→telefono (ZION::TEMP_CHAT::REQ).
  const wire = canWire ? await sendTempChatWireToContact(contactId, reqPlain) : { ok: false, delivered: false }
  const e2eOk = wire.ok
  const e2eDelivered = wire.delivered

  // Pair event backup (wake + inbox PWA); Android app usa soprattutto il wire cifrato.
  let pairOk = await sendTempChatCreate({
    toSessionToken: peerSession,
    mySessionToken: mySession,
    chatKey: session.chatKey,
    expiresAt,
    durationMs,
    myNickname: nickname,
    inviteCode: session.inviteCode,
    initiatorSessionToken: mySession,
    targetSessionToken: peerSession,
    peerNickname: contact.nickname,
  })

  if (!pairOk) {
    mySession = await ensurePrivacySession()
    await connectSocket(mySession)
    pairOk = await sendTempChatCreate({
      toSessionToken: peerSession,
      mySessionToken: mySession,
      chatKey: session.chatKey,
      expiresAt,
      durationMs,
      myNickname: nickname,
      inviteCode: session.inviteCode,
      initiatorSessionToken: mySession,
      targetSessionToken: peerSession,
      peerNickname: contact.nickname,
    })
    if (canWire && !e2eOk) {
      void sendTempChatWireToContact(contactId, reqPlain)
    }
  }

  if (!e2eOk && !pairOk) {
    removeTempChat(session.chatKey)
    return {
      ok: false,
      error: canWire
        ? 'Invito non consegnato al telefono. Tieni l\'app aperta e riaccoppia il contatto.'
        : 'Chiavi/coda mancanti. Riaccoppia il contatto dalla chat cifrata.',
    }
  }

  if (canWire && !e2eOk) {
    return {
      ok: false,
      error: 'Il wire E2E non è arrivato all\'app. Apri l\'app Android e riprova (serve socket attivo).',
    }
  }

  if (canWire && !e2eDelivered) {
    console.warn('[ZION-TEMP] wire in coda ma non consegnato live all\'app — apri l\'app Android')
  }

  void traceTempWire('INVITO_INVIATO', reqPlain, {
    chatKey: session.chatKey,
    pairOk,
    e2eOk,
    e2eDelivered,
    canWire,
    toSession: peerSession,
  })

  void wakePeerBySession(peerSession, buildNativeApnsPayload('ZION', '⏱ Invito chat a tempo'))
  void pollInboxBurst(mySession, 3, 800)
  return { ok: true, chatKey: session.chatKey }
}

export async function acceptTempChatInvite(
  chatKey: string,
): Promise<{ ok: boolean; error?: string }> {
  const key = chatKey.trim().toLowerCase()
  if (isAcceptInProgress(key)) {
    return { ok: false, error: 'Accettazione già in corso.' }
  }
  acceptInProgressKeys.add(key)
  try {
    const temp = getTempChatByKey(chatKey)
    if (!temp || temp.status !== 'pending_incoming') {
      return { ok: false, error: 'Invito non valido o già gestito.' }
    }
    const contact = await findContact(temp.contactId)
    if (!contact) return { ok: false, error: 'Contatto non trovato.' }
    const mySession = (await getSessionToken())?.trim()
    if (!mySession) return { ok: false, error: 'Sessione non pronta.' }

    const nickname = await getCurrentNickname()
    const ackPlain = buildTempChatAck(temp.chatKey)
    void traceTempWire('INVIO_ACK_SU_WIRE', ackPlain, {
      chatKey: temp.chatKey,
      targetWire: temp.chatKey,
      toSession: temp.peerSessionToken,
      contactId: temp.contactId,
    })
    const pairOk = await sendTempChatAccept({
      toSessionToken: temp.peerSessionToken,
      mySessionToken: mySession,
      chatKey: temp.chatKey,
      myNickname: nickname,
      initiatorSessionToken: temp.peerSessionToken,
      acceptorSessionToken: mySession,
      durationMs: temp.durationMs ?? Math.max(60_000, temp.expiresAt - Date.now()),
      expiresAt: temp.expiresAt,
    })
    const wireAck = await sendTempChatWireToContact(temp.contactId, ackPlain)
    const e2eOk = wireAck.ok
    void traceTempWire('INVIO_ACK_SU_WIRE_RESULT', ackPlain, {
      chatKey: temp.chatKey,
      targetWire: temp.chatKey,
      pairOk,
      e2eOk,
      delivered: wireAck.delivered,
    })
    if (!pairOk && !e2eOk) return { ok: false, error: 'Accettazione fallita.' }

    upsertTempChat({ ...temp, status: 'active' })
    markHandshakeAccepted(temp.chatKey)
    await dismissLocalPendingTempsForContact(temp.contactId, temp.chatKey)
    await appendTempSystemMessage(
      temp.chatKey,
      `Chat a tempo avviata — scade tra ${formatTempRemaining(temp.expiresAt - Date.now())}.`,
    )

    void wakePeerBySession(temp.peerSessionToken, buildNativeApnsPayload('ZION', '⏱ Chat a tempo accettata'))
    void pollInboxBurst(mySession, 4, 1500)
    return { ok: true }
  } finally {
    acceptInProgressKeys.delete(key)
  }
}

export async function rejectTempChatInvite(chatKey: string): Promise<void> {
  const temp = getTempChatByKey(chatKey)
  if (!temp || temp.status !== 'pending_incoming') return
  if (isAcceptInProgress(temp.chatKey) || isHandshakeProtected(temp.chatKey)) return
  const mySession = (await getSessionToken())?.trim()
  if (mySession) {
    void sendTempChatReject({
      toSessionToken: temp.peerSessionToken,
      mySessionToken: mySession,
      chatKey: temp.chatKey,
      initiatorSessionToken: temp.peerSessionToken,
      rejectorSessionToken: mySession,
    })
    void sendTempChatWireToContact(temp.contactId, buildTempChatRej(temp.chatKey))
  }
  await deleteMessagesForContact(tempChatMessageContactId(temp.chatKey))
  removeTempChat(temp.chatKey)
}

/** Annulla invito inviato — solo pending_outgoing (mittente). Solo wire CANCEL, mai pair reject. */
export async function cancelTempChatOutgoing(chatKey: string): Promise<void> {
  const temp = getTempChatByKey(chatKey)
  if (!temp || temp.status !== 'pending_outgoing') return
  if (isAcceptInProgress(temp.chatKey) || isHandshakeProtected(temp.chatKey)) return
  const mySession = (await getSessionToken())?.trim()
  if (mySession) {
    void sendTempChatWireToContact(temp.contactId, buildTempChatCancel(temp.chatKey))
  }
  await deleteMessagesForContact(tempChatMessageContactId(temp.chatKey))
  removeTempChat(temp.chatKey)
}

/** Termina una chat a tempo attiva (Annulla dal mittente o destinatario). Solo wire CANCEL. */
export async function endTempChatEarly(chatKey: string): Promise<void> {
  const temp = getTempChatByKey(chatKey)
  if (!temp || temp.status !== 'active') return
  if (isAcceptInProgress(temp.chatKey)) return
  const mySession = (await getSessionToken())?.trim()
  if (mySession) {
    void sendTempChatWireToContact(temp.contactId, buildTempChatCancel(temp.chatKey))
  }
  await deleteMessagesForContact(tempChatMessageContactId(temp.chatKey))
  removeTempChat(temp.chatKey)
  if (getCurrentTempChatKeyFn() === chatKey) {
    navigate('home')
  }
}

export async function sendTempChatTextByKey(chatKey: string, plainText: string): Promise<void> {
  const trimmed = plainText.trim()
  if (!trimmed) return
  const temp = getTempChatByKey(chatKey)
  if (!temp) throw new Error('Chat a tempo non trovata.')
  if (temp.status !== 'active') {
    throw new Error('In attesa che il contatto accetti la chat a tempo.')
  }
  if (temp.expiresAt <= Date.now()) {
    await handleTempChatExpired(temp.chatKey)
    throw new Error('Chat a tempo scaduta.')
  }

  const contact = await findContact(temp.contactId)
  if (!contact) throw new Error('Contatto non trovato.')
  const mySession = (await getSessionToken())?.trim()
  if (!mySession) throw new Error('Sessione non pronta.')

  const messageId = await nextMessageId()
  const msgContactId = tempChatMessageContactId(temp.chatKey)
  const msg: ChatMessage = {
    id: messageId,
    contactId: msgContactId,
    text: trimmed,
    outgoing: true,
    timestamp: Number(messageId) || Date.now(),
    pending: true,
    isTemp: true,
  }
  await appendMessage(msg)

  const msgPlain = buildTempChatMsg(temp.chatKey, messageId, trimmed)
  const wireMsg = await sendTempChatWireToContact(temp.contactId, msgPlain)
  const e2eOk = wireMsg.ok
  const pairOk = await sendTempChatMessage({
    toSessionToken: contact.sessionToken,
    mySessionToken: mySession,
    chatKey: temp.chatKey,
    message: trimmed,
    messageId,
    durationMs: temp.durationMs ?? Math.max(60_000, temp.expiresAt - Date.now()),
  })
  if (!e2eOk && !pairOk) throw new Error('Invio fallito.')
  await patchMessage(messageId, { pending: false })

  const nick = await getCurrentNickname()
  void wakePeerBySession(
    contact.sessionToken,
    buildNativeApnsPayload(nick || 'ZION', '⏱ Messaggio effimero'),
  )
  void pollInboxBurst(mySession, 3, 1500)
}

async function handleIncomingTempChatCreate(
  data: Record<string, unknown>,
  knownContact?: ZionContact,
): Promise<void> {
  const chatKey = laneFromTempEvent(data)
  const expiresAt = Number(data.expiresAt) || Date.now() + TEMP_CHAT_FALLBACK_MS
  const durationMs =
    resolveDurationMsFromPayload(data) ??
    (Number(data.durationMs) || undefined) ??
    Math.max(60_000, expiresAt - Date.now())
  const fromSession = knownContact
    ? knownContact.sessionToken.trim()
    : await resolveTempEventPeerSession(data, { preferInitiator: true })
  if (!chatKey || !fromSession) return

  void traceTempWire('RICEVUTO_DA_TEL_PAIR', `ZION::TEMP_CHAT::REQ|${chatKey}`, {
    chatKey,
    fromSession,
    source: knownContact ? 'e2e-decrypted' : 'pair-socket',
  })

  if (!(await isTempControlFromPeer(fromSession))) return

  const existing = getTempChatByKey(chatKey)
  if (existing?.status === 'pending_outgoing') return
  if (existing?.status === 'active') return

  const contact = knownContact ?? (await findContactForTempChat(fromSession))
  if (!contact) return

  const mySession = (await getSessionToken())?.trim() ?? ''
  if (sessionsEqual(contact.sessionToken, mySession)) return

  const fromNick = String(data.myNickname ?? data.nickname ?? contact.nickname ?? '').trim()
  const inviteCode = String(data.inviteCode ?? '').trim() || undefined
  upsertTempChat({
    contactId: contact.contactId,
    chatKey,
    peerSessionToken: fromSession,
    expiresAt,
    status: 'pending_incoming',
    durationMs,
    fromNickname: fromNick,
    inviteCode,
  })

  void notifyIncomingMessage(contact, `⏱ Invito chat a tempo (${durationLabel(durationMs)})`)
  navigate('home')
}

async function handleIncomingTempChatAccept(data: Record<string, unknown>): Promise<void> {
  const rawKey = laneFromTempEvent(data)
  const fromSession = await resolveTempEventPeerSession(data, { preferAcceptor: true })
  if (!fromSession && !rawKey) {
    console.warn('[ZION-TEMP] ACK senza peer né lane', data)
    return
  }

  void traceTempWire('RICEVUTO_ACK_PAIR', `ZION::TEMP_CHAT::ACK|${rawKey}`, {
    chatKey: rawKey,
    fromSession,
    source: 'pair-socket',
  })

  const contact = fromSession ? await findContactBySession(fromSession) : null

  // Peer first: create/accept possono usare lane diverse sul server.
  let temp = fromSession ? findPendingOutgoingTempForPeer(fromSession) : null
  if (!temp && contact) {
    temp =
      listPendingOutgoingTempChats().find((s) => s.contactId === contact.contactId) ?? null
  }
  if (!temp && rawKey) {
    const byLane = findPendingOutgoingByLane(rawKey)
    if (byLane) temp = byLane
  }
  if (!temp && contact && rawKey) {
    const resolvedKey = resolveTempChatKey(contact.contactId, rawKey)
    const byResolved = getTempChatByKey(resolvedKey)
    if (byResolved?.status === 'pending_outgoing') temp = byResolved
  }
  if (!temp) {
    const outgoing = listPendingOutgoingTempChats()
    if (outgoing.length === 1) temp = outgoing[0]!
  }

  if (!temp || temp.status !== 'pending_outgoing') {
    console.warn('[ZION-TEMP] ACK ignorato — nessun pending_outgoing', {
      rawKey,
      fromSession: fromSession?.slice(0, 8),
      pending: listPendingOutgoingTempChats().map((s) => ({
        chatKey: s.chatKey,
        peer: s.peerSessionToken.slice(0, 8),
      })),
    })
    return
  }

  const chatKey = temp.chatKey
  const resolvedMs =
    resolveDurationMsFromPayload(data) ??
    temp.durationMs ??
    Math.max(60_000, temp.expiresAt - Date.now())
  const expiresAt = temp.expiresAt > Date.now() ? temp.expiresAt : Date.now() + resolvedMs
  upsertTempChat({ ...temp, status: 'active', durationMs: resolvedMs, expiresAt })
  setMessageTimerTtl(temp.contactId, resolvedMs)
  markHandshakeAccepted(chatKey)
  await dismissLocalPendingTempsForContact(temp.contactId, chatKey)
  const durationNote = temp.durationMs ? durationLabel(temp.durationMs) : formatTempRemaining(temp.expiresAt - Date.now())
  await appendTempSystemMessage(
    chatKey,
    `Chat a tempo accettata — durata ${durationNote}, scade tra ${formatTempRemaining(temp.expiresAt - Date.now())}.`,
  )

  navigate('tempChat', { chatKey })
}

async function handleIncomingTempChatReject(data: Record<string, unknown>): Promise<void> {
  const rawKey = laneFromTempEvent(data)
  if (rawKey && isHandshakeProtected(rawKey)) {
    console.warn('[ZION-TEMP] reject ignorato — handshake protetto:', rawKey)
    return
  }

  const userInitiated = data.userInitiated === true
  const fromSession = await resolveTempEventPeerSession(data)
  if (!userInitiated && fromSession && !(await isTempControlFromPeer(fromSession))) return

  const contact = fromSession ? await findContactBySession(fromSession) : null
  const chatKey =
    contact && rawKey
      ? resolveTempChatKey(contact.contactId, rawKey)
      : rawKey
  if (!chatKey) return
  if (isHandshakeProtected(chatKey)) {
    console.warn('[ZION-TEMP] reject ignorato — handshake protetto:', chatKey)
    return
  }
  const temp = getTempChatByKey(chatKey)
  if (!temp) return
  if (temp.status === 'active') {
    console.warn('[ZION-TEMP] reject ignorato — chat già attiva:', chatKey)
    return
  }
  if (temp.status !== 'pending_outgoing' && temp.status !== 'pending_incoming') return

  await deleteMessagesForContact(tempChatMessageContactId(chatKey))
  removeTempChat(chatKey)

  if (getCurrentTempChatKeyFn() === chatKey) {
    navigate('home')
  }
}

async function handleIncomingTempMessage(data: Record<string, unknown>): Promise<void> {
  const rawKey = String(data.chatKey ?? '').trim()
  const message = String(data.message ?? '').trim()
  const fromSession = String(data.fromSessionToken ?? data.mySessionToken ?? '').trim()
  const messageId = String(data.messageId ?? Date.now()).trim()
  if (!rawKey || !message || !fromSession) return

  const contact: ZionContact | null = (await findContactBySession(fromSession)) ?? null
  if (!contact) return

  const chatKey = resolveTempChatKey(contact.contactId, rawKey)

  let temp = getTempChatByKey(chatKey)
  if (!temp) {
    upsertTempChat({
      contactId: contact.contactId,
      chatKey,
      peerSessionToken: fromSession,
      expiresAt: Date.now() + TEMP_CHAT_FALLBACK_MS,
      status: 'active',
    })
    temp = getTempChatByKey(chatKey)!
  } else if (temp.status === 'pending_incoming' || temp.status === 'pending_outgoing') {
    upsertTempChat({ ...temp, status: 'active' })
    temp = getTempChatByKey(chatKey)!
  }

  if (temp.status !== 'active') return

  const msgContactId = tempChatMessageContactId(chatKey)
  if (await messageExists(messageId)) return

  const msg: ChatMessage = {
    id: messageId,
    contactId: msgContactId,
    text: message,
    outgoing: false,
    timestamp: Number(messageId) || Date.now(),
    isTemp: true,
  }
  await appendMessage(msg)

  const viewingThisTemp =
    document.visibilityState === 'visible' && getCurrentTempChatKeyFn() === chatKey
  if (!viewingThisTemp) incrementUnread(tempChatUnreadId(chatKey))
  void notifyTempIncomingMessage(contact, chatKey, message)
}

/** Se c'è una temp attiva col contatto, instrada messaggio plain lì (non chat normale). */
export async function routePlainToActiveTempChat(
  contact: ZionContact,
  plain: string,
  messageId: string,
): Promise<boolean> {
  const text = plain.trim()
  if (!text || parseTempChatControl(text)) return false
  const active = findActiveTempForContact(contact.contactId)
  if (!active) return false
  await handleIncomingTempMessage({
    chatKey: active.chatKey,
    message: text,
    fromSessionToken: contact.sessionToken,
    messageId,
  })
  return true
}

export async function handleTempChatExpired(chatKey: string): Promise<void> {
  const key = chatKey.trim()
  if (!key) return
  const temp = getTempChatByKey(key)
  if (!temp) return

  await deleteMessagesForContact(tempChatMessageContactId(key))
  clearUnread(tempChatUnreadId(key))
  removeTempChat(key)

  const mySession = (await getSessionToken())?.trim()
  if (mySession && temp.peerSessionToken) {
    void sendTempChatExpired({
      toSessionToken: temp.peerSessionToken,
      mySessionToken: mySession,
      chatKey: key,
    })
  }

  if (getCurrentTempChatKeyFn() === key) {
    navigate('home')
  }
}

async function purgeExpiredTempChats(): Promise<void> {
  const now = Date.now()
  for (const temp of listTempChats()) {
    if (temp.expiresAt <= now) {
      await handleTempChatExpired(temp.chatKey)
    }
  }
}

export {
  getTempChatByKey,
  listTempChats,
  listPendingIncomingTempChats,
} from '../storage/tempChatStore.ts'

export { formatTempRemaining } from './tempChatUtils.ts'

export type { TempChatSession }
